import { useState, useMemo } from 'react';
import { paginationConfig } from '../../../../config/ui/pagination-config';

/**
 * Reusable pagination hook for any table with count data
 * 
 * @template T - The data type returned by the list hook
 * @template F - The filter type for the query
 */
export interface UsePaginatedDataOptions<T, F> {
  /** Hook to fetch paginated list data */
  useListHook: (options: {
    limit: number;
    offset: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    filters?: F;
  }) => {
    data: T[];
    isLoading: boolean;
    error: any;
    refetch: () => void;
  };
  
  /** Hook to fetch total count with same filters */
  useCountHook: (filters?: F) => {
    data: number;
  };
  
  /** Initial filters to apply */
  initialFilters?: F;
  
  /** Initial page size (defaults to config) */
  initialPageSize?: number;
  
  /** Initial sort column */
  initialSortBy?: string;
  
  /** Initial sort direction */
  initialSortOrder?: 'asc' | 'desc';
}

/**
 * Reusable hook that provides pagination state and data fetching for any entity
 * 
 * This hook standardizes pagination patterns across all tables in the application.
 * It automatically handles:
 * - Page state management
 * - Data fetching with proper pagination
 * - Total count with consistent filters
 * - Previous/next page detection
 * - Page size changes with reset to page 1
 * 
 * @example
 * ```typescript
 * const {
 *   // Data
 *   data: users,
 *   totalCount,
 *   isLoading,
 *   error,
 *   
 *   // Pagination state
 *   currentPage,
 *   pageSize,
 *   totalPages,
 *   hasNextPage,
 *   hasPrevPage,
 *   
 *   // Actions
 *   setCurrentPage,
 *   setPageSize,
 *   setSortBy,
 *   setSortOrder,
 *   setFilters,
 *   refetch
 * } = usePaginatedData({
 *   useListHook: useUserProfilesList,
 *   useCountHook: useUserProfilesCount,
 *   initialFilters: { status: 'active' },
 *   initialSortBy: 'created_at',
 *   initialSortOrder: 'desc'
 * });
 * ```
 */
export function usePaginatedData<T, F extends Record<string, any> = Record<string, any>>({
  useListHook,
  useCountHook,
  initialFilters = {} as F,
  initialPageSize = paginationConfig.defaults.pageSize,
  initialSortBy,
  initialSortOrder = 'desc'
}: UsePaginatedDataOptions<T, F>) {
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortOrder, setSortOrder] = useState(initialSortOrder);
  const [filters, setFilters] = useState<F>(initialFilters);

  // Memoized query options to prevent unnecessary re-renders
  const queryOptions = useMemo(() => ({
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    sortBy,
    sortOrder,
    filters
  }), [currentPage, pageSize, sortBy, sortOrder, filters]);

  // Fetch data using provided hooks
  const { data, isLoading, error, refetch } = useListHook(queryOptions);
  const { data: totalCount = 0 } = useCountHook(filters);

  // Calculate pagination info
  const totalPages = Math.ceil(totalCount / pageSize);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  // Enhanced setters that handle page resets appropriately
  const handleSetPageSize = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleSetFilters = (newFilters: F) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when changing filters
  };

  const handleSetSortBy = (newSortBy: string) => {
    setSortBy(newSortBy);
    // Don't reset page when just changing sort
  };

  const handleSetSortOrder = (newOrder: 'asc' | 'desc') => {
    setSortOrder(newOrder);
    // Don't reset page when just changing sort order
  };

  return {
    // Data
    data: data || [],
    totalCount,
    isLoading,
    error,
    
    // Pagination state (read-only)
    currentPage,
    pageSize,
    totalPages,
    hasNextPage,
    hasPrevPage,
    sortBy,
    sortOrder,
    filters,
    
    // Actions
    setCurrentPage,
    setPageSize: handleSetPageSize,
    setSortBy: handleSetSortBy,
    setSortOrder: handleSetSortOrder,
    setFilters: handleSetFilters,
    refetch,
    
    // Computed values for pagination component
    paginationProps: {
      currentPage,
      totalPages,
      totalItems: totalCount,
      pageSize,
      hasNextPage,
      hasPrevPage
    }
  };
}

/**
 * Specialized hook for search-enabled tables
 * 
 * Extends usePaginatedData with search functionality and proper filter management.
 * 
 * @example
 * ```typescript
 * const {
 *   searchTerm,
 *   setSearchTerm,
 *   ...paginationProps
 * } = usePaginatedSearch({
 *   useListHook: useUserProfilesList,
 *   useCountHook: useUserProfilesCount,
 *   searchFields: ['full_name', 'email'], // Optional: specific fields to search
 *   initialSortBy: 'created_at'
 * });
 * ```
 */
export function usePaginatedSearch<T, F extends Record<string, any> = Record<string, any>>({
  useListHook,
  useCountHook,
  searchFields,
  initialFilters = {} as F,
  ...otherOptions
}: UsePaginatedDataOptions<T, F> & {
  /** Specific fields to search (optional - uses API default if not provided) */
  searchFields?: string[];
}) {
  const [searchTerm, setSearchTerm] = useState('');

  // Combine search with other filters
  const searchFilters = useMemo(() => {
    const combined: Record<string, any> = { ...initialFilters };

    if (searchTerm.trim()) {
      // Add search to filters - the API will handle the actual search logic
      combined.search = searchTerm.trim();

      // If specific search fields are provided, include them
      if (searchFields && searchFields.length > 0) {
        combined.searchFields = searchFields.join(',');
      }
    }

    return combined as F;
  }, [initialFilters, searchTerm, searchFields]);

  const paginationResult = usePaginatedData({
    useListHook,
    useCountHook,
    initialFilters: searchFilters,
    ...otherOptions
  });

  // Override setFilters to preserve search term
  const handleSetFilters = (newFilters: F) => {
    const filtersWithSearch: Record<string, any> = { ...newFilters };
    if (searchTerm.trim()) {
      filtersWithSearch.search = searchTerm.trim();
      if (searchFields && searchFields.length > 0) {
        filtersWithSearch.searchFields = searchFields.join(',');
      }
    }
    paginationResult.setFilters(filtersWithSearch as F);
  };

  const handleSetSearchTerm = (term: string) => {
    setSearchTerm(term);
    // Search change resets to page 1
    paginationResult.setCurrentPage(1);
  };

  return {
    ...paginationResult,
    searchTerm,
    setSearchTerm: handleSetSearchTerm,
    setFilters: handleSetFilters
  };
}